import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal, Backdrop } from '@sendible/design-system';
import { getDownloadUrl, useGoogleDriveService } from '@sendible/frontendv2/src/utils/googleAuthentication/useGoogleDriveService';
import { GoogleService } from '@sendible/frontendv2/src/utils/googleAuthentication/types';
import { useComposeBoxContext } from '../../../composeBoxContext';
import { ConnectService, GoogleDriveCenter } from '../styles/index.styles';

export const GoogleDriveModal = () => {
  const { setIsGoogleDriveModalOpen, uploadUrlsToCompose } = useComposeBoxContext();
  const { t } = useTranslation(['common_terms', 'compose_box']);
  const { isAuthenticatedPickerReady, isLoading, authorizeGoogleDrive, openDrivePicker, closePicker, logout } = useGoogleDriveService();

  const handleConnect = async () => {
    await authorizeGoogleDrive();
  };

  const handleSave = useCallback(
    (files: { id: string; name?: string }[]) => {
      closePicker();
      uploadUrlsToCompose(
        files.map((file) => ({
          url: getDownloadUrl(file.id),
          name: file.name,
        }))
      );
      setIsGoogleDriveModalOpen(false);
    },
    [closePicker, uploadUrlsToCompose, setIsGoogleDriveModalOpen]
  );

  useEffect(() => {
    if (isAuthenticatedPickerReady) {
      const handlePickerOpen = async () => {
        try {
          await openDrivePicker(handleSave, () => {
            setIsGoogleDriveModalOpen(false);
          });
        } catch {
          logout(GoogleService.Picker);
          closePicker();
        }
      };

      handlePickerOpen();

      return () => {
        closePicker();
      };
    }
  }, [isAuthenticatedPickerReady, openDrivePicker, closePicker, logout]);

  if (isAuthenticatedPickerReady) {
    return (
      <Backdrop
        handleClick={() => {
          closePicker();
          setIsGoogleDriveModalOpen(false);
        }}
        useBackground={false}
      />
    );
  }

  return (
    <Modal
      portalDomId="layer-root"
      close={() => {
        closePicker();
        setIsGoogleDriveModalOpen(false);
      }}
      headingIcon="google_drive"
      headingTitle={t('google_drive') as string}
      variation="large"
    >
      <GoogleDriveCenter>
        <ConnectService>
          <Button
            icon="google_color"
            label={t('continue_with_google') as string}
            onClick={handleConnect}
            size={16}
            variation="outline"
            isLoading={isLoading}
          />
        </ConnectService>
      </GoogleDriveCenter>
    </Modal>
  );
};
